import React, { useMemo } from 'react';

import {
  Box,
  DialogContentText,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  useTheme,
} from '@material-ui/core';
import dayjs from 'dayjs';
import { BiIdCard } from 'react-icons/bi';
import clsx from 'clsx';

import { ImageUploader } from '@vyce/core/src/components';
import { getUKFormattedDate } from '@vyce/core/src/utils/dates';
import { TextStatusComponent } from '@vyce/core/src/components/Statuses/TextStatusComponent';
import { Image } from '@vyce/core/src/types';

import useStyles from '../styles';
import { QualificationType, CertificateOptions, TestOption, CourseOption } from '../types';
import { dateFields, previewControlSet } from '../config';

const commonSize = 120;

const certificateOptions: CertificateOptions[] = [
  { fieldName: 'title', title: 'Qualification Name' },
  { fieldName: 'issuer', title: 'Issuing Body' },
  { fieldName: 'verified', title: 'Status' },
  { fieldName: 'expiry_date', title: 'Expiry' },
  { fieldName: 'reference_number', title: 'Reference number' },
];

const testOptions: TestOption[] = [
  { fieldName: 'title', title: 'Qualification Name' },
  { fieldName: 'issuer', title: 'Issuing Body' },
  { fieldName: 'verified', title: 'Status' },
  { fieldName: 'exam_date', title: 'Awarded Date' },
];

const courseOptions: CourseOption[] = [
  { fieldName: 'title', title: 'Qualification Name' },
  { fieldName: 'issuer', title: 'Issuing Body' },
  { fieldName: 'verified', title: 'Status' },
  { fieldName: 'expiry_date', title: 'Expiry' },
];

const CommonQualificationContentItem = ({
  fieldName,
  title,
  value,
  children,
}: {
  fieldName: string;
  title: string;
  value: string;
  children?: React.ReactNode;
}) => {
  const classes = useStyles();
  const theme = useTheme();

  return value ? (
    <Box display="flex" flexDirection="column">
      <Typography
        className={classes.smallText}
        style={{ color: theme.palette.type === 'dark' ? '#A0A3BD' : '#6E7191' }}>
        {title}
      </Typography>
      {children ? (
        children
      ) : (
        <Typography className={classes.smallText}>
          {dateFields.includes(fieldName) ? getUKFormattedDate(value) : value}
        </Typography>
      )}
      {fieldName === 'expiry_date' && !value && (
        <Typography className={classes.smallText}>No Expiry</Typography>
      )}
    </Box>
  ) : null;
};

export const CommonQualificationContent = ({ qualification }: { qualification: QualificationType }) => {
  const classes = useStyles();

  const previewDocument: Image | null = useMemo(() => {
    if (qualification.type === 'certificate' && qualification?.document?.url) {
      return { url: qualification?.document?.url, name: qualification?.document?.url } as Image;
    }
    return null;
  }, [qualification]);
  return (
    <>
      <Box flex="1 0 auto">
        {qualification && 'endorsements' in qualification && !!qualification?.endorsements?.length && (
          <>
            <Typography>Endorsements:</Typography>

            <List>
              {qualification.endorsements.map((item: any, index: number) => (
                <ListItem key={index}>
                  <ListItemAvatar>
                    <BiIdCard size="32px" />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.title}
                    secondary={`Expiry Date: ${dayjs(item.expiry_date).format('DD MMMM YYYY')}`}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
        <Box display="flex" flexDirection="column" gridGap={16} marginTop={2}>
          {qualification.type === 'certificate' &&
            certificateOptions.map(({ fieldName, title }) => (
              <CommonQualificationContentItem
                key={fieldName}
                fieldName={fieldName}
                title={title}
                value={(qualification[fieldName] as string) ?? ''}>
                {fieldName === 'verified' ? (
                  <TextStatusComponent
                    verified={qualification.verified}
                    expiry_date={qualification.expiry_date}
                  />
                ) : null}
              </CommonQualificationContentItem>
            ))}

          {qualification.type === 'test' &&
            testOptions.map(({ fieldName, title }) => (
              <CommonQualificationContentItem
                key={fieldName}
                fieldName={fieldName}
                title={title}
                value={(qualification[fieldName] as string) ?? ''}
              />
            ))}

          {qualification.type === 'course' &&
            courseOptions.map(({ fieldName, title }) => (
              <CommonQualificationContentItem
                key={fieldName}
                fieldName={fieldName}
                title={title}
                value={(qualification[fieldName] as string) ?? ''}
              />
            ))}
        </Box>
      </Box>
      {qualification.type === 'certificate' && qualification?.document?.url && (
        <Box className={classes.documentsPreviewWrapper}>
          <ImageUploader
            image={previewDocument}
            extraText="new files"
            extraSmallSize={true}
            isDocument={true}
            type="both"
            outsideControl
            customControlSet={previewControlSet}
            width={commonSize}
            height={commonSize}
          />
        </Box>
      )}
    </>
  );
};
